import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { KnowledgeGraph } from '@lizrd/contracts';
import { getDomainAssessment, getEstimateConfidence, getRecommendations } from '../lib/api';
import { Card } from './ui/card';
import { Stat } from './ui/stat';
import { CostArchitecture } from './CostArchitecture';
import { EstimateConfidenceModal } from './EstimateConfidenceModal';
import { UnbilledCloudsNote } from './UnbilledCloudsNote';
import { Cost } from './ui/Cost';
import { CostCategoryIcon } from './node-icons';
import { categoryOf, CLOUD_PROVIDERS, relativeTime, usd } from '../lib/graph-visuals';

type GraphNode = KnowledgeGraph['nodes'][number];

// Cloud infrastructure asset types (excludes code, dependencies, IaC, accounts).
const INFRA_TYPES = [
  'compute.instance',
  'compute.function',
  'compute.workload',
  'container.cluster',
  'database',
  'cache.cluster',
  'storage.volume',
  'storage.snapshot',
  'storage.bucket',
  'network.nat',
  'network.address',
  'network.loadbalancer',
  'network.endpoint',
  'network.gateway',
];

// Second-level resource categories, in reading order (emoji from the shared map).
const CATEGORY_ORDER = ['Compute', 'Databases', 'Cache', 'Storage', 'Networking'];

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Join clauses naturally: "a", "a and b", "a, b and c". */
function joinClauses(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** 2–3 plain-language sentences describing the domain, from the counts. */
function describeArchitecture(
  counts: Record<string, number>,
  clouds: string[],
  repoCount: number,
  infraTotal: number,
  monthlyCost: number,
): string {
  const sentences: string[] = [];
  const cloudPhrase = clouds.length
    ? `across ${plural(clouds.length, 'cloud')} (${clouds.join(', ')})`
    : 'across your cloud';
  sentences.push(
    `Lizrd has mapped ${plural(infraTotal, 'infrastructure resource')} ${cloudPhrase}` +
      (repoCount ? `, linked to ${plural(repoCount, 'code base')}.` : '.'),
  );

  const composition: string[] = [];
  if (counts.Compute) composition.push(plural(counts.Compute, 'compute resource'));
  if (counts.Databases) composition.push(plural(counts.Databases, 'database'));
  if (counts.Cache) composition.push(plural(counts.Cache, 'cache cluster'));
  if (counts.Storage) composition.push(plural(counts.Storage, 'storage resource'));
  if (composition.length) sentences.push(`It runs ${joinClauses(composition)}.`);

  const tail: string[] = [];
  if (counts.Networking) tail.push(`networking spans ${plural(counts.Networking, 'resource')}`);
  if (monthlyCost > 0) tail.push(`estimated spend is ${usd(monthlyCost)}/month`);
  if (tail.length) sentences.push(`${cap(joinClauses(tail))}.`);

  return sentences.join(' ');
}

/**
 * The domain cockpit: top-level counts (code bases, infrastructure, clouds,
 * cost), the architecture graph, and a resource-composition + plain-language
 * summary, plus a read-only "last sync" indicator (syncs run automatically on a
 * schedule). Utilization and cost trends live on the domain Overview dashboard,
 * so this focuses on "what you run".
 */
export function DomainSummary({
  envId,
  graph,
  totalMonthlyCost,
  lastSyncedAt,
}: {
  envId: string;
  graph: KnowledgeGraph | null;
  totalMonthlyCost: number;
  lastSyncedAt: string | null;
}) {
  const nodes: GraphNode[] = graph?.nodes ?? [];
  const confidence = useQuery({
    queryKey: ['estimate-confidence', envId],
    queryFn: () => getEstimateConfidence(envId),
    enabled: !!envId,
    refetchInterval: 60_000,
  });
  // Billed cost from the billing-grade ledger (calendar month) — month-to-date +
  // projected — so the Explorer headline matches the Dashboard and Billing page.
  const assessment = useQuery({
    queryKey: ['domain-assessment', envId],
    queryFn: () => getDomainAssessment(envId),
    enabled: !!envId,
    refetchInterval: 60_000,
    // Stable across remounts/refetches so the headline never flashes the estimate
    // before the billed run rate settles.
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const billed = assessment.data?.billedMonthlyCost ?? null;
  const unbilledClouds = assessment.data?.unbilledClouds ?? [];
  const recommendations = useQuery({
    queryKey: ['recommendations', envId],
    queryFn: () => getRecommendations(envId),
    enabled: !!envId,
    refetchInterval: 60_000,
  });
  const [confidenceOpen, setConfidenceOpen] = useState(false);

  // Aggregate the graph into the counts the cockpit renders.
  const byCategory: Record<string, number> = {};
  const repoNames: string[] = [];
  const cloudSet = new Set<string>();
  let infraTotal = 0;
  for (const n of nodes) {
    if (n.type === 'repository') repoNames.push(n.name);
    if (CLOUD_PROVIDERS.has(n.provider)) cloudSet.add(n.provider);
    if (INFRA_TYPES.includes(n.type)) {
      const cat = categoryOf(n.type);
      byCategory[cat] = (byCategory[cat] ?? 0) + 1;
      infraTotal++;
    }
  }
  const clouds = [...cloudSet].sort();
  const categories = CATEGORY_ORDER.filter((name) => byCategory[name]);
  const architecture = describeArchitecture(
    byCategory,
    clouds,
    repoNames.length,
    infraTotal,
    totalMonthlyCost,
  );

  return (
    <Card className="space-y-5 p-5">
      {/* Header + primary action, top-aligned. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-bold text-bone">Domain Overview</h2>
          <p className="text-xs text-muted">
            What Lizrd has discovered across your code and cloud.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {/* Syncs run automatically on a schedule; we just show when it last completed. */}
          <span className="text-[0.7rem] text-muted">Last sync {relativeTime(lastSyncedAt)}</span>
        </div>
      </div>

      {/* Level 1 — the headline counts. */}
      <div className="flex flex-col divide-y divide-hairline sm:flex-row sm:divide-x sm:divide-y-0">
        <Stat
          className="flex-1 px-5 py-3 sm:px-6 sm:py-1 sm:first:pl-0"
          value={repoNames.length}
          label="Code bases"
          sub={
            repoNames.length ? (
              <span className="block truncate" title={repoNames.join(', ')}>
                {repoNames[0]}
                {repoNames.length > 1 ? ` +${repoNames.length - 1}` : ''}
              </span>
            ) : (
              'None linked yet'
            )
          }
        />
        <Stat
          className="flex-1 px-5 py-3 sm:px-6 sm:py-1"
          value={infraTotal}
          label="Infra resources"
          sub={
            clouds.length
              ? `across ${clouds.length} cloud${clouds.length === 1 ? '' : 's'}`
              : undefined
          }
        />
        <Stat
          className="flex-1 px-5 py-3 sm:px-6 sm:py-1"
          value={clouds.length}
          label="Clouds"
          sub={clouds.length ? clouds.join(' · ') : 'None connected'}
        />
        <Stat
          className="flex-1 px-5 py-3 sm:px-6 sm:py-1"
          value={
            <Cost
              value={billed != null && billed > 0 ? billed : totalMonthlyCost}
              provenance={billed != null && billed > 0 ? 'billed' : 'estimated'}
              confidence={confidence.data?.level}
              onExplain={billed != null && billed > 0 ? undefined : () => setConfidenceOpen(true)}
            />
          }
          label="Monthly cost"
          accent
          sub={
            billed != null && billed > 0
              ? assessment.data?.projectedBilledCost
                ? `~${usd(assessment.data.projectedBilledCost)}/mo run rate`
                : 'month-to-date'
              : totalMonthlyCost > 0
                ? 'estimated — tap the tag'
                : 'awaiting cost data'
          }
        />
      </div>
      {billed != null && billed > 0 && <UnbilledCloudsNote clouds={unbilledClouds} />}
      {confidenceOpen && confidence.data && (
        <EstimateConfidenceModal data={confidence.data} onClose={() => setConfidenceOpen(false)} />
      )}

      {/* The architecture graph — your world at a glance (moved here from the cost
          views). Utilization + cost trends now live on the Overview dashboard. */}
      {graph && <CostArchitecture graph={graph} envId={envId} />}

      {/* Level 2 — composition + a plain-language architecture summary. */}
      {infraTotal > 0 && (
        <div className="space-y-3 border-t border-hairline pt-4">
          <div>
            <h3 className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
              Resource composition
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {categories.map((name) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-2 rounded-full border border-hairline bg-surface-2 px-3 py-1.5"
                >
                  <CostCategoryIcon category={name} className="h-5 w-5 text-muted" />
                  <span className="font-display text-base font-bold text-bone">
                    {byCategory[name]}
                  </span>
                  <span className="text-xs text-haze">{name}</span>
                </span>
              ))}
            </div>
          </div>
          <p className="max-w-3xl text-sm leading-relaxed text-haze">{architecture}</p>
        </div>
      )}

      {/* Cost-optimization nudge — actionable link into the tab, at the very foot. */}
      {recommendations.data && recommendations.data.count > 0 && (
        <Link
          to="../cost-optimization"
          className="flex items-center gap-3 rounded-md border border-iris/30 bg-iris/5 p-3 transition-colors hover:bg-iris/10"
        >
          <Lightbulb aria-hidden className="h-6 w-6 flex-none text-iris" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-bone">
              {recommendations.data.count} optimization
              {recommendations.data.count === 1 ? '' : 's'}
            </span>
            <span className="text-haze">
              {' '}
              — up to{' '}
              <span className="font-display text-iris">
                {usd(recommendations.data.totalMonthlySaving)}/mo
              </span>{' '}
              in potential savings.
            </span>
          </div>
          <span className="text-xs font-semibold text-viridian">Review →</span>
        </Link>
      )}
    </Card>
  );
}
